<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import integrationAPI from 'dashboard/api/integrations';
import {
  eventDisplayName as displayNameForEvent,
  eventInventoryLabel as inventoryLabelForEvent,
  eventLineItemCount,
  eventMetadataSummary as metadataSummaryForEvent,
  eventProductImageAlt as productImageAltForEvent,
  eventProductImageUrl,
  eventProductUrl,
  eventValueLabel,
} from 'dashboard/components-next/Contacts/helpers/klaviyoEventMetadata';
import CrmEventCreateDialog from 'dashboard/components-next/Contacts/CrmEvents/CrmEventCreateDialog.vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

/**
 * @typedef {Object} KlaviyoContactProfile
 * @property {string|null} email
 * @property {string|null} phone_number
 * @property {string|null} matched_by
 * @property {string|null} last_synced_at
 */

/**
 * @typedef {Object} KlaviyoSubscriptionState
 * @property {number|string} id
 * @property {'email'|'sms'} channel
 * @property {string} message_type
 * @property {string} local_intent
 * @property {string|null} remote_consent
 * @property {boolean|null} remote_can_receive
 * @property {string} sync_status
 * @property {Array} remote_suppression
 * @property {Array} remote_list_suppressions
 * @property {string|null} provider_error
 * @property {string|null} remote_last_updated
 */

/**
 * @typedef {Object} KlaviyoEvent
 * @property {number|string} id
 * @property {string|null} event_name
 * @property {string} metric_name
 * @property {string} occurred_at
 * @property {string|number|null} value
 * @property {Record<string, unknown>} metadata
 */

/**
 * @typedef {Object} KlaviyoCrmProfilePayload
 * @property {string} status
 * @property {boolean} integration_available
 * @property {KlaviyoContactProfile|null} profile
 * @property {KlaviyoSubscriptionState[]} subscription_states
 * @property {KlaviyoEvent[]} events
 */

/**
 * Contact sidebar panel that shows the latest cached Klaviyo CRM profile.
 */
const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const REFRESH_INTERVAL_MS = 3000;
const MAX_REFRESH_ATTEMPTS = 20;

const { t } = useI18n();
const isLoading = ref(false);
const submittingChannel = ref('');
/** @type {import('vue').Ref<KlaviyoCrmProfilePayload|null>} */
const klaviyoCrmProfile = ref(null);
const errorMessage = ref('');
const isKlaviyoUnavailable = ref(false);
const refreshTimer = ref(null);
const remainingRefreshAttempts = ref(0);
const createDialogRef = ref(null);
/** @type {import('vue').Ref<Set<string>>} */
const failedProductImageEventIds = ref(new Set());

/** @type {import('vue').ComputedRef<KlaviyoContactProfile|null|undefined>} */
const klaviyoProfile = computed(() => klaviyoCrmProfile.value?.profile);
/** @type {import('vue').ComputedRef<KlaviyoEvent[]>} */
const klaviyoEvents = computed(() => klaviyoCrmProfile.value?.events || []);
/** @type {import('vue').ComputedRef<KlaviyoSubscriptionState[]>} */
const subscriptionStates = computed(
  () => klaviyoCrmProfile.value?.subscription_states || []
);
const hasLoadedCrmProfile = computed(() => Boolean(klaviyoCrmProfile.value));
const hasAvailableKlaviyoIntegration = computed(
  () => klaviyoCrmProfile.value?.integration_available !== false
);
const hasKlaviyoProfile = computed(() => Boolean(klaviyoProfile.value));
const subscriptionStatusLabels = computed(() => ({
  subscribed: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.SUBSCRIBED'),
  unsubscribed: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.UNSUBSCRIBED'),
  neverSubscribed: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.NEVER_SUBSCRIBED'),
  pendingOutbound: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.PENDING_OUTBOUND'),
  submitted: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.SUBMITTED'),
  pendingConfirmation: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.PENDING_CONFIRMATION'
  ),
  requiresAction: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.REQUIRES_ACTION'),
  conflict: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.CONFLICT'),
  failed: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.FAILED'),
  unknown: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.UNKNOWN'),
}));
const subscriptionActionLabels = computed(() => ({
  subscribe: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.ACTIONS.SUBSCRIBE'),
  resubscribe: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.ACTIONS.RESUBSCRIBE'),
  unsubscribe: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.ACTIONS.UNSUBSCRIBE'),
}));
const subscriptionTooltipKeys = computed(() => ({
  subscribed: 'SUBSCRIBED',
  unsubscribed: 'UNSUBSCRIBED',
  neverSubscribed: 'NEVER_SUBSCRIBED',
  pendingOutbound: 'PENDING_OUTBOUND',
  submitted: 'SUBMITTED',
  pendingConfirmation: 'PENDING_CONFIRMATION',
  requiresAction: 'REQUIRES_ACTION',
  conflict: 'CONFLICT',
  failed: 'FAILED',
  unknown: 'UNKNOWN',
}));
const subscriptionTooltipLabels = computed(() => ({
  SUBSCRIBED: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.SUBSCRIBED'),
  UNSUBSCRIBED: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.UNSUBSCRIBED'),
  NEVER_SUBSCRIBED: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.NEVER_SUBSCRIBED'
  ),
  PENDING_OUTBOUND: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.PENDING_OUTBOUND'
  ),
  SUBMITTED: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.SUBMITTED'),
  PENDING_CONFIRMATION: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.PENDING_CONFIRMATION'
  ),
  REQUIRES_ACTION: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.REQUIRES_ACTION'
  ),
  CONFLICT: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.CONFLICT'),
  FAILED: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.FAILED'),
  UNKNOWN: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.UNKNOWN'),
}));
/**
 * Finds the current marketing subscription state for a channel.
 *
 * @param {string} channel Email or SMS channel.
 * @returns {KlaviyoSubscriptionState|undefined}
 */
function subscriptionStateFor(channel) {
  return subscriptionStates.value.find(
    state => state.channel === channel && state.message_type === 'marketing'
  );
}

const subscriptionChannels = computed(() =>
  [
    {
      channel: 'email',
      label: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.EMAIL'),
      identifier: props.selectedContact?.email,
      icon: 'i-lucide-mail',
    },
    {
      channel: 'sms',
      label: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.SMS'),
      identifier: props.selectedContact?.phoneNumber,
      icon: 'i-lucide-message-square',
    },
  ]
    .map(item => ({
      ...item,
      state: subscriptionStateFor(item.channel),
    }))
    .filter(
      item =>
        hasLoadedCrmProfile.value &&
        hasAvailableKlaviyoIntegration.value &&
        (item.identifier || item.state)
    )
);
const hasSubscriptionChannels = computed(
  () => subscriptionChannels.value.length > 0
);
const shouldRenderPanel = computed(() => {
  if (isKlaviyoUnavailable.value) return false;
  if (isLoading.value || errorMessage.value) return true;
  if (
    hasKlaviyoProfile.value ||
    hasSubscriptionChannels.value ||
    klaviyoEvents.value.length
  )
    return true;
  return hasLoadedCrmProfile.value && hasAvailableKlaviyoIntegration.value;
});

/**
 * Checks whether the CRM profile endpoint reports Klaviyo as unavailable.
 *
 * @param {unknown} error API error raised by Axios.
 * @returns {boolean}
 */
const isKlaviyoUnavailableResponse = error =>
  error?.response?.data?.code === 'klaviyo_disabled_for_account';

/**
 * Returns the canonical display name for a parsed CRM event.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {string}
 */
const eventDisplayName = event =>
  displayNameForEvent(event, t('CONTACT_PANEL.CRM.EVENT'));

/**
 * Builds a compact commerce summary from typed event metadata.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {string}
 */
function eventMetadataSummary(event) {
  const lineItemCount = eventLineItemCount(event);
  return metadataSummaryForEvent(event, {
    itemCountLabel: lineItemCount
      ? t('CONTACT_PANEL.CRM.ITEM_COUNT', { count: lineItemCount })
      : null,
    inventoryLabel: inventoryLabelForEvent(event, {
      availableLabel: ({ count }) =>
        t('CONTACT_PANEL.CRM.INVENTORY_AVAILABLE', { count }),
      availableAtLocationLabel: ({ count, location }) =>
        t('CONTACT_PANEL.CRM.INVENTORY_AVAILABLE_AT_LOCATION', {
          count,
          location,
        }),
      availableAtLocationsLabel: ({ count, locationCount }) =>
        t('CONTACT_PANEL.CRM.INVENTORY_AVAILABLE_AT_LOCATIONS', {
          count,
          locationCount,
        }),
    }),
  });
}

/**
 * Returns the product detail URL for a parsed event when Klaviyo supplied one.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {string|null}
 */
const eventProductImageAlt = event =>
  productImageAltForEvent(event, t('CONTACT_PANEL.CRM.EVENT'));

/**
 * Builds a stable key for image-failure tracking.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {string}
 */
const imageFailureKey = event => String(event.id || event.occurred_at);

/**
 * Checks whether the product image already failed in this render session.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {boolean}
 */
const hasProductImageFailed = event =>
  failedProductImageEventIds.value.has(imageFailureKey(event));

/**
 * Hides a broken product image and keeps the event row readable.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {void}
 */
function markProductImageFailed(event) {
  failedProductImageEventIds.value = new Set([
    ...failedProductImageEventIds.value,
    imageFailureKey(event),
  ]);
}

/**
 * Opens the manual CRM event dialog with the current contact preselected.
 *
 * @returns {void}
 */
function openCreateDialog() {
  createDialogRef.value?.open();
}

/**
 * Stops scheduled background refreshes for this contact.
 *
 * @returns {void}
 */
function stopRefreshPolling() {
  if (refreshTimer.value) window.clearTimeout(refreshTimer.value);
  refreshTimer.value = null;
  remainingRefreshAttempts.value = 0;
}

/**
 * Hides the panel when Klaviyo becomes unavailable for the account.
 *
 * @returns {void}
 */
const hideUnavailableKlaviyoPanel = () => {
  klaviyoCrmProfile.value = null;
  isKlaviyoUnavailable.value = true;
  errorMessage.value = '';
  stopRefreshPolling();
};

/**
 * Loads the selected contact's locally cached Klaviyo profile.
 *
 * @returns {Promise<void>}
 */
const loadKlaviyoProfile = async () => {
  if (!props.selectedContact?.id) return;

  isLoading.value = true;
  errorMessage.value = '';
  isKlaviyoUnavailable.value = false;
  failedProductImageEventIds.value = new Set();
  try {
    const klaviyoProfileResponse =
      await integrationAPI.getKlaviyoContactProfile(props.selectedContact.id);
    klaviyoCrmProfile.value = klaviyoProfileResponse.data;
  } catch (error) {
    if (isKlaviyoUnavailableResponse(error)) {
      hideUnavailableKlaviyoPanel();
      return;
    }

    errorMessage.value = t('CONTACT_PANEL.CRM.ERROR');
  } finally {
    isLoading.value = false;
  }
};

/**
 * Refreshes CRM profile activity after a manual event is created.
 *
 * @returns {Promise<void>}
 */
async function handleEventCreated() {
  await loadKlaviyoProfile();
}

/**
 * Maps backend sync statuses to computed label keys.
 *
 * @param {string} syncStatus Backend sync status.
 * @returns {string}
 */
function syncStatusLabelKey(syncStatus) {
  return (
    {
      pending_outbound: 'pendingOutbound',
      submitted: 'submitted',
      pending_confirmation: 'pendingConfirmation',
      requires_action: 'requiresAction',
      conflict: 'conflict',
      failed: 'failed',
    }[syncStatus] || ''
  );
}

/**
 * Checks if Klaviyo reports channel or list suppression.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {boolean}
 */
const hasSuppression = state =>
  Boolean(
    state?.remote_suppression?.length || state?.remote_list_suppressions?.length
  );

/**
 * Checks whether a subscription action is still being processed.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {boolean}
 */
const isSubscriptionActionPending = state =>
  ['pending_outbound', 'submitted', 'pending_confirmation'].includes(
    state?.sync_status
  );
const hasPendingSubscriptionAction = computed(() =>
  subscriptionStates.value.some(state => isSubscriptionActionPending(state))
);

/**
 * Builds the operator-facing status label for a subscription state.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {string}
 */
const subscriptionStatusLabel = state => {
  if (!state) return subscriptionStatusLabels.value.unknown;

  const syncStatusLabel =
    subscriptionStatusLabels.value[syncStatusLabelKey(state.sync_status)];
  if (syncStatusLabel) return syncStatusLabel;

  if (state.remote_consent === 'SUBSCRIBED' && state.remote_can_receive) {
    return subscriptionStatusLabels.value.subscribed;
  }
  if (state.remote_consent === 'UNSUBSCRIBED') {
    return subscriptionStatusLabels.value.unsubscribed;
  }
  if (hasSuppression(state))
    return subscriptionStatusLabels.value.requiresAction;

  if (state.remote_consent === 'NEVER_SUBSCRIBED') {
    return subscriptionStatusLabels.value.neverSubscribed;
  }
  return subscriptionStatusLabels.value.unknown;
};

/**
 * Builds the translation key suffix for the operator status tooltip.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {string}
 */
const subscriptionStatusTooltipKey = state => {
  if (!state) return subscriptionTooltipKeys.value.unknown;

  const syncStatusKey = syncStatusLabelKey(state.sync_status);
  if (syncStatusKey) return subscriptionTooltipKeys.value[syncStatusKey];

  if (state.remote_consent === 'SUBSCRIBED' && state.remote_can_receive) {
    return subscriptionTooltipKeys.value.subscribed;
  }
  if (state.remote_consent === 'UNSUBSCRIBED') {
    return subscriptionTooltipKeys.value.unsubscribed;
  }
  if (hasSuppression(state))
    return subscriptionTooltipKeys.value.requiresAction;
  if (state.remote_consent === 'NEVER_SUBSCRIBED') {
    return subscriptionTooltipKeys.value.neverSubscribed;
  }
  return subscriptionTooltipKeys.value.unknown;
};

/**
 * Builds operator-facing help text for a subscription status.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {string}
 */
const subscriptionStatusTooltip = state => {
  const baseTooltip =
    subscriptionTooltipLabels.value[subscriptionStatusTooltipKey(state)] ||
    subscriptionTooltipLabels.value.UNKNOWN;
  if (!state?.provider_error) return baseTooltip;

  return t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.PROVIDER_ERROR', {
    base: baseTooltip,
    error: state.provider_error,
  });
};

/**
 * Resolves the next explicit consent action for a channel state.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {'subscribe'|'resubscribe'|'unsubscribe'}
 */
const nextSubscriptionAction = state => {
  if (state?.remote_consent === 'SUBSCRIBED' && state?.remote_can_receive) {
    return 'unsubscribe';
  }
  if (
    state?.remote_consent === 'UNSUBSCRIBED' ||
    state?.sync_status === 'requires_action' ||
    hasSuppression(state)
  ) {
    return 'resubscribe';
  }
  return 'subscribe';
};

/**
 * Refreshes pending subscription states without flashing the full panel spinner.
 *
 * @returns {Promise<void>}
 */
const refreshPendingSubscriptionState = async () => {
  if (!props.selectedContact?.id) {
    stopRefreshPolling();
    return;
  }

  try {
    const response = await integrationAPI.getKlaviyoContactProfile(
      props.selectedContact.id
    );
    klaviyoCrmProfile.value = response.data;
  } catch (error) {
    if (isKlaviyoUnavailableResponse(error)) {
      hideUnavailableKlaviyoPanel();
      return;
    }

    stopRefreshPolling();
    return;
  }

  remainingRefreshAttempts.value -= 1;
  if (
    hasPendingSubscriptionAction.value &&
    remainingRefreshAttempts.value > 0
  ) {
    refreshTimer.value = window.setTimeout(
      refreshPendingSubscriptionState,
      REFRESH_INTERVAL_MS
    );
  } else {
    stopRefreshPolling();
  }
};

/**
 * Starts short polling while Klaviyo/provider readback is pending.
 *
 * @returns {void}
 */
function scheduleRefreshPolling() {
  if (refreshTimer.value) return;

  remainingRefreshAttempts.value = MAX_REFRESH_ATTEMPTS;
  refreshTimer.value = window.setTimeout(
    refreshPendingSubscriptionState,
    REFRESH_INTERVAL_MS
  );
}

/**
 * Sends an explicit operator subscription action to the backend.
 *
 * @param {Object} item Rendered subscription channel item.
 * @returns {Promise<void>}
 */
const submitSubscriptionAction = async item => {
  const action = nextSubscriptionAction(item.state);
  submittingChannel.value = item.channel;
  try {
    const response = await integrationAPI.captureKlaviyoSubscriptionConsent(
      props.selectedContact.id,
      {
        channel: item.channel,
        consent_action: action,
        evidence: {
          ui: 'contact_klaviyo_panel',
        },
      }
    );
    klaviyoCrmProfile.value = response.data;
    if (hasPendingSubscriptionAction.value) scheduleRefreshPolling();
    useAlert(t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.API.SUCCESS'));
  } catch (error) {
    if (isKlaviyoUnavailableResponse(error)) {
      hideUnavailableKlaviyoPanel();
      return;
    }

    useAlert(
      error?.response?.data?.message ||
        t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.API.ERROR')
    );
  } finally {
    submittingChannel.value = '';
  }
};

onMounted(loadKlaviyoProfile);
onBeforeUnmount(stopRefreshPolling);

watch(
  () => props.selectedContact?.id,
  () => {
    stopRefreshPolling();
    loadKlaviyoProfile();
  }
);

watch(hasPendingSubscriptionAction, pending => {
  if (pending) {
    scheduleRefreshPolling();
  } else {
    stopRefreshPolling();
  }
});
</script>

<template>
  <div
    v-if="shouldRenderPanel && isLoading"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else-if="shouldRenderPanel" class="flex flex-col gap-4 px-6 py-4">
    <p v-if="errorMessage" class="text-sm text-n-ruby-9">
      {{ errorMessage }}
    </p>

    <div v-if="hasKlaviyoProfile" class="flex flex-col gap-4">
      <section class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-3">
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('CONTACT_PANEL.CRM.KLAVIYO_PROFILE') }}
          </h3>
          <span
            class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
          >
            {{ klaviyoCrmProfile.status }}
          </span>
        </div>
        <dl class="grid gap-2 text-sm">
          <div>
            <dt class="text-n-slate-10">
              {{ t('CONTACT_PANEL.EMAIL_ADDRESS') }}
            </dt>
            <dd class="text-n-slate-12">
              {{ klaviyoProfile.email || t('CONTACT_PANEL.NOT_AVAILABLE') }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-10">
              {{ t('CONTACT_PANEL.PHONE_NUMBER') }}
            </dt>
            <dd class="text-n-slate-12">
              {{
                klaviyoProfile.phone_number || t('CONTACT_PANEL.NOT_AVAILABLE')
              }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-10">
              {{ t('CONTACT_PANEL.CRM.MATCHED_BY') }}
            </dt>
            <dd class="text-n-slate-12">
              {{
                klaviyoProfile.matched_by || t('CONTACT_PANEL.NOT_AVAILABLE')
              }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-10">
              {{ t('CONTACT_PANEL.CRM.LAST_SYNCED') }}
            </dt>
            <dd class="text-n-slate-12">
              {{
                klaviyoProfile.last_synced_at ||
                t('CONTACT_PANEL.NOT_AVAILABLE')
              }}
            </dd>
          </div>
        </dl>
      </section>
    </div>

    <section v-if="hasSubscriptionChannels" class="flex flex-col gap-2">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TITLE') }}
      </h3>
      <div class="flex flex-col gap-2">
        <div
          v-for="item in subscriptionChannels"
          :key="item.channel"
          class="flex items-start justify-between gap-3 p-3 rounded-lg bg-n-alpha-2"
        >
          <div class="min-w-0">
            <div class="inline-flex items-center gap-2">
              <span class="size-4 text-n-slate-10" :class="item.icon" />
              <p class="text-sm font-medium text-n-slate-12">
                {{ item.label }}
              </p>
            </div>
            <p class="mt-1 text-xs break-all text-n-slate-10">
              {{ item.identifier || t('CONTACT_PANEL.NOT_AVAILABLE') }}
            </p>
            <div class="flex items-center gap-1.5 mt-1 text-xs text-n-slate-11">
              <p>
                {{ subscriptionStatusLabel(item.state) }}
              </p>
              <span
                class="mb-2 size-3.5 shrink-0 text-n-slate-10 i-lucide-info"
                :title="subscriptionStatusTooltip(item.state)"
                :aria-label="subscriptionStatusTooltip(item.state)"
              />
            </div>
            <p
              v-if="item.state?.provider_error"
              class="mt-1 text-xs break-words text-n-ruby-9"
            >
              {{ item.state.provider_error }}
            </p>
          </div>
          <Button
            v-if="!isSubscriptionActionPending(item.state)"
            xs
            faded
            slate
            class="shrink-0"
            :label="
              subscriptionActionLabels[nextSubscriptionAction(item.state)]
            "
            :is-loading="submittingChannel === item.channel"
            :disabled="Boolean(submittingChannel) || !item.identifier"
            @click="submitSubscriptionAction(item)"
          />
        </div>
      </div>
    </section>

    <section class="flex flex-col gap-2">
      <div class="flex items-center justify-between gap-3">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ t('CONTACT_PANEL.CRM.EVENTS') }}
        </h3>
        <Button
          xs
          faded
          slate
          icon="i-lucide-plus"
          :label="t('CRM_EVENT_CREATE.OPEN_SHORT')"
          @click="openCreateDialog"
        />
      </div>
      <div v-if="klaviyoEvents.length" class="divide-y divide-n-weak">
        <div
          v-for="klaviyoEvent in klaviyoEvents"
          :key="klaviyoEvent.id"
          class="flex items-center justify-between gap-3 py-3"
        >
          <div class="flex min-w-0 items-start gap-3">
            <div
              class="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-n-alpha-2 text-n-slate-10"
            >
              <img
                v-if="
                  eventProductImageUrl(klaviyoEvent) &&
                  !hasProductImageFailed(klaviyoEvent)
                "
                data-testid="klaviyo-product-image"
                class="size-full object-cover"
                :src="eventProductImageUrl(klaviyoEvent)"
                :alt="eventProductImageAlt(klaviyoEvent)"
                loading="lazy"
                @error="markProductImageFailed(klaviyoEvent)"
              />
              <span
                v-else
                data-testid="klaviyo-product-image-fallback"
                class="i-lucide-image size-4"
              />
            </div>
            <div class="min-w-0">
              <a
                v-if="eventProductUrl(klaviyoEvent)"
                data-testid="klaviyo-product-link"
                class="block truncate text-sm font-medium text-n-slate-12 hover:text-n-brand"
                :href="eventProductUrl(klaviyoEvent)"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="
                  t('CONTACT_PANEL.CRM.OPEN_PRODUCT', {
                    name: eventDisplayName(klaviyoEvent),
                  })
                "
              >
                {{ eventDisplayName(klaviyoEvent) }}
              </a>
              <p v-else class="text-sm font-medium truncate text-n-slate-12">
                {{ eventDisplayName(klaviyoEvent) }}
              </p>
              <p
                v-if="eventMetadataSummary(klaviyoEvent)"
                class="text-xs truncate text-n-slate-11"
              >
                {{ eventMetadataSummary(klaviyoEvent) }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ klaviyoEvent.occurred_at }}
              </p>
            </div>
          </div>
          <span
            v-if="eventValueLabel(klaviyoEvent)"
            class="text-sm text-n-slate-11"
          >
            {{ eventValueLabel(klaviyoEvent) }}
          </span>
        </div>
      </div>
      <p v-else class="py-4 text-sm text-center text-n-slate-11">
        {{ t('CONTACT_PANEL.CRM.NO_EVENTS') }}
      </p>
    </section>

    <p
      v-if="
        hasAvailableKlaviyoIntegration &&
        !hasKlaviyoProfile &&
        !hasSubscriptionChannels &&
        !klaviyoEvents.length
      "
      class="py-6 text-sm text-center text-n-slate-11"
    >
      {{ t('CONTACT_PANEL.CRM.EMPTY') }}
    </p>
    <CrmEventCreateDialog
      ref="createDialogRef"
      :preselected-contact="selectedContact"
      @created="handleEventCreated"
    />
  </div>
</template>
